/**
 * RightPanel Component - Combat and Character Management Sidebar
 * 
 * This component provides the right sidebar for the BattleRoom, containing all the panels
 * needed for managing characters, combat, and communication during D&D sessions.
 * 
 * PANELS INCLUDED:
 * - Character Sheet: Displays full character stats and abilities for the selected token
 * - Initiative Tracker: Manages turn order and combat flow with real token data
 * - Token Properties: Allows editing of selected token's combat stats (HP, AC, etc.)
 * - Chat: Communication panel for players and DM
 * 
 * SMART BEHAVIOR:
 * - Only shows character/token data for actual combatants (players, monsters, NPCs)
 * - Map assets show a special "Map Asset Selected" message instead of properties
 * - Token Properties panel updates in real-time when tokens are selected
 * - Initiative Tracker automatically syncs with token changes
 * 
 * INTEGRATION:
 * - Receives selectedToken from BattleRoom to know which token is active
 * - Gets full tokens array to populate Initiative Tracker
 * - Calls onTokenChange when user edits token properties
 * - Uses CollapsiblePanel for space-efficient expandable sections
 */

import React from 'react';
import BattleRoomCharacterSheet from './BattleRoomCharacterSheet';
import MonsterSheetViewer from '../../MonsterSheetViewer/MonsterSheetViewer';
import ChatPanel from './ChatPanel';
import ResizablePanel from './ResizablePanel';
import TokenPropertiesContent from '../../TokenPropertiesPopup/TokenPropertiesContent';
import CombatLog from '../../CombatLog/CombatLog';
import LatestResolutionCard from './LatestResolutionCard';
import './RightPanel.css';

const RightPanel = ({ selectedToken, selectedTokens, onCombatantChange, onTokenChange, tokens, pinnedTokenProperties, onTokenPropertiesClose, onTokenPropertiesUpdate, onTokenSizeChange, onTokenPropertiesTogglePin, 
  // @date_of_change 2025-09-12 - Programmatic expand when using icon mode right rail
  // Shape: { section: 'sheet' | 'log' | 'chat', ts: number }
  expandSection,
  // @date_of_change: 2025-10-09 - Role/ownership gating for viewing sheets and GM controls
  isGameMaster = false,
  canControlToken = null,
  currentUser = null,
  combatLogEditMode = false,
  onCombatLogEditModeChange = null,
  authoritativeRewindPoints = null,
  rewindPending = false,
  rewindError = '',
  onRefreshRewindPoints = null,
  onRewindRequest = null,
  onRewindPreview = null,
  getTokenControlInfo = () => ({ controlled: false, displayText: 'Unassigned', color: '#gray' })
}) => {
  // Handle both selectedToken and selectedTokens props for backward compatibility
  // If selectedToken is null but selectedTokens has items, use the first item
  const activeToken = selectedToken || (selectedTokens && selectedTokens.length > 0 ? selectedTokens[0] : null);
  
  // @date_of_change: 2025-10-09 - Determine if current viewer can see the selected token's sheet
  const canViewSheet = (() => {
    if (!activeToken) return false;
    if (isGameMaster) return true;
    const tType = (activeToken.type || '').toLowerCase();
    if (tType === 'monster') return false; // Players cannot view monster stat blocks
    if (typeof canControlToken === 'function') {
      try {
        if (canControlToken(activeToken.id)) return true;
      } catch (_) {}
    }
    try {
      const info = getTokenControlInfo(activeToken.id);
      return info?.displayText === 'All Players' || (info?.userId && info.userId === currentUser?.userId);
    } catch {
      return false;
    }
  })();

  return (
    <div className={`right-panel ${!isGameMaster ? 'non-gm' : ''}`}>
      {/* Immediate feedback is intentionally separate from the durable log and
          the selected creature sheet. It explains only the latest committed
          action and never becomes another mutable source of combat state.
          date_of_change: 2026-08-11 */}
      <LatestResolutionCard tokens={tokens} />

      <ResizablePanel 
        title={activeToken?.type === 'monster' ? "Monster Stat Block" : "Character Sheet"} 
        icon={null}
        defaultExpanded={false}
        defaultHeight={400}
        minHeight={200}
        maxHeight={800}
        expandSignal={expandSection?.section === 'sheet' ? expandSection.ts : undefined}
      >
        <div className="character-sheet-container">
          {activeToken ? (
            canViewSheet ? (
              <>
                {activeToken.type === 'monster' ? (
                  // GM only path; players won't reach here due to canViewSheet check
                  <MonsterSheetViewer 
                    token={{...activeToken, templateId: activeToken.dashboardId}}
                  />
                ) : (
                  <BattleRoomCharacterSheet 
                    token={activeToken}
                    focusTab={expandSection?.section === 'sheet' ? expandSection?.tab : null}
                  />
                )}
              </>
            ) : (
              <div className="no-token-selected">
                {/* @date_of_change: 2025-10-09 - Hide monster or non-owned token sheets for players */}
                <p>Data hidden for this token</p>
                <p>{(activeToken.type || '').toLowerCase() === 'monster' ? 'Players cannot view monster data.' : 'You can only view your own tokens or those shared with All Players.'}</p>
              </div>
            )
          ) : (
            <div className="no-token-selected">
              <p>No token selected</p>
              <p>Select a character or monster to view details</p>
            </div>
          )}
        </div>
      </ResizablePanel>

      {/* Pinned Token Properties Panel */}
      {pinnedTokenProperties && pinnedTokenProperties.visible && pinnedTokenProperties.isPinned && (
        <ResizablePanel 
          title="Token Properties (Pinned)" 
          icon={null}
          defaultExpanded={true}
          defaultHeight={350}
          minHeight={200}
          maxHeight={600}
        >
          <TokenPropertiesContent
            token={pinnedTokenProperties.tokenId ? tokens.find(t => t.id === pinnedTokenProperties.tokenId) : null}
            onUpdate={onTokenPropertiesUpdate}
            onTokenSizeChange={onTokenSizeChange}
            showHeader={true}
            onClose={onTokenPropertiesClose}
            isPinned={true}
            onTogglePin={() => {
              // Toggle back to popup mode - this should unpin and show as popup
              if (onTokenPropertiesTogglePin) {
                onTokenPropertiesTogglePin();
              }
            }}
            isGameMaster={isGameMaster}
          />
        </ResizablePanel>
      )}

      <ResizablePanel 
        title="Combat Log" 
        icon={null}
        defaultExpanded={false}
        defaultHeight={400}
        minHeight={200}
        maxHeight={600}
        expandSignal={expandSection?.section === 'log' ? expandSection.ts : undefined}
      >
        <CombatLog 
          // @date_of_change: 2025-10-09 - Hide GM controls in log for players
          isGameMaster={isGameMaster}
          editMode={combatLogEditMode}
          onEditModeChange={onCombatLogEditModeChange}
          currentUser={currentUser}
          authoritativeRewindPoints={authoritativeRewindPoints}
          rewindPending={rewindPending}
          rewindError={rewindError}
          onRefreshRewindPoints={onRefreshRewindPoints}
          onRewindRequest={onRewindRequest}
          onRewindPreview={onRewindPreview}
          onExportSession={(sessionData) => {
            // Handle combat session export
            console.log('[RightPanel] Combat session export:', sessionData);
            // Default behavior handled by CombatLog component
          }}
        />
      </ResizablePanel>

      <ResizablePanel 
        title="Chat" 
        icon={null}
        defaultExpanded={false}
        defaultHeight={300}
        minHeight={150}
        maxHeight={500}
        expandSignal={expandSection?.section === 'chat' ? expandSection.ts : undefined}
      >
        <ChatPanel />
      </ResizablePanel>
    </div>
  );
};

export default RightPanel;
